import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { AdminProgressRow } from "@equipped/core/backend/client";
import { useAuth } from "../auth/AuthContext";
import { userMatchesQuery } from "../lib/userSearch";
import { MainCard } from "../components/MainCard";

/** Mirrors `apps/admin-web/src/pages/ProgressPage.tsx`'s `detailSummary` verbatim. */
function detailSummary(row: AdminProgressRow): string {
  const d = row.detail ?? {};
  if (row.kind === "quiz") {
    const best = d.bestPercent;
    const attempts = d.attempts;
    return typeof best === "number" ? `${best}% best${typeof attempts === "number" ? ` · ${attempts} attempt(s)` : ""}` : "quiz";
  }
  if (row.kind === "assessment") {
    const cats = d.topCategories;
    if (Array.isArray(cats) && cats.length > 0) {
      return cats
        .map((c) => (c && typeof c === "object" && "title" in c ? String((c as { title: unknown }).title) : null))
        .filter((t): t is string => !!t)
        .join(", ");
    }
    return "assessment";
  }
  const times = d.timesCompleted;
  return typeof times === "number" && times > 1 ? `completed ×${times}` : "completed";
}

/**
 * Progress tab, re-skinned to MUI/Mantis (docs/337-ADMIN-MANTIS-MIGRATION.md v2) — mirrors
 * `apps/admin-web/src/pages/ProgressPage.tsx` (itself mirroring `apps/admin/app/(tabs)/progress.tsx` /
 * `specs/admin/screens/progress.md`): a read-only "which student completed what" list over opt-in
 * submitted progress (docs/304), cross-referencing `userId` against the People list for an email. Every
 * `useQuery` here is lifted UNCHANGED — only the returned JSX differs.
 */
export function ProgressPage() {
  const { client, token } = useAuth();
  const [query, setQuery] = useState("");

  const progressQuery = useQuery({
    queryKey: ["admin-progress"],
    queryFn: () => client!.adminProgress(token!),
    enabled: !!client && !!token,
  });
  const usersQuery = useQuery({
    queryKey: ["admin-users", false],
    queryFn: () => client!.adminUsers(token!),
    enabled: !!client && !!token,
  });

  const progress = progressQuery.data ?? [];
  const users = usersQuery.data ?? [];
  const emailById = useMemo(() => new Map(users.map((u) => [u.id, u.email])), [users]);
  const filteredUserIds = useMemo(() => new Set(users.filter((u) => userMatchesQuery(u, query)).map((u) => u.id)), [users, query]);
  const filtered = useMemo(
    () => (query.trim() ? progress.filter((p) => filteredUserIds.has(p.userId)) : progress),
    [progress, query, filteredUserIds],
  );

  return (
    <MainCard title="Student progress">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Only shows accounts that opted in to sharing progress with this provider.
      </Typography>
      <TextField
        size="small"
        fullWidth
        placeholder="Filter by name or email…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ mb: 2 }}
      />
      {progressQuery.isError ? <Alert severity="error">{(progressQuery.error as Error).message}</Alert> : null}
      {progressQuery.isLoading ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 0 }}>
          No progress submitted yet.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {filtered.map((row) => (
            <Box key={row.id} sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
              <Typography sx={{ mb: 0.5 }}>
                {emailById.get(row.userId) ?? row.userId} · {row.title}
                {row.packTitle ? ` (${row.packTitle})` : ""}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {row.kind} · {detailSummary(row)} · {new Date(row.lastAt).toLocaleDateString()}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </MainCard>
  );
}
